import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { auth } from '../lib/api';
import { useErrorText } from '../lib/errors';
import { useT } from '../lib/i18n';
import { useSession } from '../lib/session';

/**
 * Where the link in the confirmation mail lands.
 *
 * It works signed in or out — mail is read on the phone as often as at the
 * desk — so it never assumes a session. The token is spent on arrival; the
 * page only reports what happened and offers the way on.
 */
export function VerifyPage() {
  const t = useT();
  const describe = useErrorText();
  const { user, refresh } = useSession();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [state, setState] = useState<'working' | 'done' | 'failed'>('working');
  const [error, setError] = useState('');
  const [resent, setResent] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setState('failed');
      setError(t('auth.verifyFailed'));
      return;
    }
    auth
      .verify(token)
      .then(async () => {
        await refresh();
        if (!cancelled) setState('done');
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setState('failed');
        setError(describe(err, 'auth.verifyFailed'));
      });
    return () => {
      cancelled = true;
    };
    // The token is read once, on arrival.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const resend = async () => {
    try {
      await auth.resendVerification();
      setResent(true);
    } catch (err) {
      setError(describe(err, 'auth.failed'));
    }
  };

  return (
    <div className="auth auth--single">
      <div className="auth__card stack">
        <h1>{t('auth.verifyTitle')}</h1>
        {state === 'working' && <p className="muted">{t('auth.verifying')}</p>}
        {state === 'done' && (
          <>
            <p>{t('auth.verified')}</p>
            <Link className="btn btn--primary" to="/new">
              {t('auth.continue')}
            </Link>
          </>
        )}
        {state === 'failed' && (
          <>
            <div className="banner banner--error">{error}</div>
            {user && !user.emailVerified && !resent && (
              <button type="button" className="btn" onClick={resend}>
                {t('auth.verifyResend')}
              </button>
            )}
            {resent && <p className="muted">{t('auth.verifySent')}</p>}
            <Link className="btn" to={user ? '/new' : '/'}>
              {user ? t('auth.continue') : t('auth.backToSignIn')}
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
